import {
  FunnelStatusTerminalOutcome,
  type FunnelStatus,
} from 'src/api/generated';
import { FunnelStatusForm } from './status-form';
import type { FunnelStatusFormValues } from './status-form';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { FunnelStatusPermission } from 'src/modules/shared/types/permissions.type';
import {
  useUpdateFunnelStatus,
  useDeleteFunnelStatus,
} from 'src/modules/shared/hooks/api/crm/funnels/funnel-status.mutation';
import { FunnelStatusCard } from 'src/modules/settings/components/atoms/funnels/statuses/status-card';
import { UnsavedChangesModal } from 'src/modules/shared/components/molecules/modals/unsaved-changes-modal';
import { ConfirmModal } from 'src/modules/shared/components/organisms/modal';
import { useState } from 'react';
import type { DraggableChildrenProps } from 'src/modules/shared/components/molecules/draggable-item';
import { type StatusReference } from '../../../../types/funnels/statuses/status-description.types';
import { useAlert } from 'src/contexts/alert-context';
import { extractApiErrorMessage } from 'src/modules/shared/utils/api/error-handler.util';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

const LINKED_CONVERSATIONS_DELETE_ERROR =
  'Cannot delete this funnel status because it has associated conversations. Reassign those conversations to another status before deleting it.';

/**
 * Props for the FunnelStatusEditCard component.
 *
 * Manages the display and editing of a funnel status with support for drag-and-drop
 * reordering, form state tracking, and reference highlighting in descriptions.
 */
interface FunnelStatusEditCardProps {
  /** The funnel status object with optional description text */
  status: FunnelStatus & { description?: string | null };
  /** Whether the status card is currently in edit mode */
  isEditing?: boolean;
  /** Whether the status card is currently being dragged */
  isDragging?: boolean;
  /** Optional drag handle component for reordering statuses */
  DragHandle?: DraggableChildrenProps['DragHandle'];
  /** Additional CSS classes to apply to the card container */
  className?: string;
  /** Array of status references that can be linked or highlighted in descriptions */
  referenceStatuses?: StatusReference[];
  /** Callback fired when the form dirty state changes (true = unsaved changes exist) */
  onDirtyChange?: (dirty: boolean) => void;
  /** Whether the status currently has pending unsaved changes */
  hasUnsavedChanges?: boolean;
  /** Whether this status is linked to an AI employee workspace */
  isAiLinked?: boolean;
}

/**
 * Renders an edit card for a funnel status, including a form for editing the status
 * and a delete confirmation modal.
 *
 * @param status - The status to edit
 * @param isEditing - Whether the status is currently being edited
 * @param isDragging - Whether the status is being dragged
 * @param DragHandle - The component to render as the drag handle
 * @param className - Additional CSS classes to apply to the card
 * @param referenceStatuses - The statuses that can be referenced in the description
 * @param onDirtyChange - A callback to notify when the status has unsaved changes
 * @param hasUnsavedChanges - Whether the status has unsaved changes
 * @returns A React node containing the edit card
 */
export const FunnelStatusEditCard = ({
  status,
  isEditing = false,
  isDragging = false,
  DragHandle,
  className = '',
  referenceStatuses,
  onDirtyChange,
  hasUnsavedChanges = false,
  isAiLinked = false,
}: FunnelStatusEditCardProps) => {
  const { t } = useI18n();
  const { showAlert } = useAlert();
  const { hasPermission: canUpdate } = useHasPermissions(
    FunnelStatusPermission.Update
  );
  const { hasPermission: canDelete } = useHasPermissions(
    FunnelStatusPermission.Delete
  );
  const { hasPermission: canManageSystemStatuses } = useHasPermissions(
    FunnelStatusPermission.ManageSystem
  );
  const canManageStatus = !status.systemKey || canManageSystemStatuses;

  const { updateStatus, loading: isUpdating } = useUpdateFunnelStatus();
  const { deleteStatus, loading: isDeleting } = useDeleteFunnelStatus(
    () => undefined,
    (error) => {
      const message = extractApiErrorMessage(error);
      showAlert(
        message === LINKED_CONVERSATIONS_DELETE_ERROR
          ? t('settings.funnels.statusDelete.error.linkedConversations')
          : message,
        'error'
      );
    }
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBasicConfirm, setShowBasicConfirm] = useState(false);

  const handleStatusUpdate = async (data: FunnelStatusFormValues) => {
    await updateStatus({
      ...data,
      terminalOutcome: data.terminalOutcome
        ? data.terminalOutcome === 'positive'
          ? FunnelStatusTerminalOutcome.POSITIVE
          : FunnelStatusTerminalOutcome.NEGATIVE
        : null,
      id: status.id,
      funnelId: status.funnelId,
      position: status.position,
    });
  };

  const handleStatusDelete = () => {
    if (hasUnsavedChanges) {
      setShowDeleteConfirm(true);
      return;
    }
    setShowBasicConfirm(true);
  };

  const handleConfirmedDelete = async () => {
    try {
      await deleteStatus(status.id);
    } catch {
      // The mutation onError handler already surfaces the API message to the user.
    }
  };

  const DragHandleComponent =
    canUpdate && canManageStatus && isEditing ? DragHandle : undefined;

  return (
    <FunnelStatusCard
      isHighlighted={isDragging}
      DragHandle={DragHandleComponent}
      className={className}
    >
      <FunnelStatusForm
        status={status}
        onSubmit={handleStatusUpdate}
        id={`status-form-${status.id}`}
        editMode={isEditing && canManageStatus}
        showDelete={canDelete && canManageStatus}
        onDelete={handleStatusDelete}
        isLoading={isUpdating || isDeleting}
        referenceStatuses={referenceStatuses}
        onDirtyChange={onDirtyChange}
        isAiLinked={isAiLinked}
      />
      <UnsavedChangesModal
        isOpen={showDeleteConfirm}
        onProceed={async () => {
          setShowDeleteConfirm(false);
          await handleConfirmedDelete();
        }}
        onStay={() => setShowDeleteConfirm(false)}
      />

      <ConfirmModal
        isOpen={showBasicConfirm}
        onClose={() => setShowBasicConfirm(false)}
        title={t('settings.funnels.statusDelete.confirm.title')}
        variant="danger"
        size="sm"
        onConfirm={async () => {
          setShowBasicConfirm(false);
          await handleConfirmedDelete();
        }}
        confirmText={t('shared.actions.delete')}
        cancelText={t('shared.actions.cancel')}
        loading={isDeleting}
      >
        <div className="text-gray-700 text-sm">
          {t('settings.funnels.statusDelete.confirm.body')}
        </div>
      </ConfirmModal>
    </FunnelStatusCard>
  );
};
